import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Popup } from 'react-leaflet';
import { SignalGroup } from '../../deviceState/SignalGroup';
import {
    getCongestion,
    getDeviceState,
    getQueueLength,
    getTrafficAmount, setCongestion,
    setDeviceState, setQueueLength,
    setTrafficAmount,
    resetDeviceStateRetryCount,
    selectDeviceStateError
} from '../../deviceState/deviceStateSlice';
import { TrafficStatistics } from '../../deviceState/TrafficStatistics';
import Collapsible from '../../ui/Collapsible';
import { IntersectionInfo } from '../../deviceState/IntersectionInfo';

const TrafficLightMarkerPopup = (props) => {
    const { id, properties } = props.data;
    const dispatch = useDispatch();
    const deviceId = properties['liva_nro'];
    const deviceStateError = useSelector(selectDeviceStateError);
    const [isOpen, setIsOpen] = useState(false);

    const handleDeviceStateFetch = useCallback((deviceId) => {
        return dispatch(getDeviceState(deviceId));
    }, [dispatch]);

    // Manage polling based on open state and errors
    useEffect(() => {
        let timerId;
        let isActive = true;

        const poll = async () => {
            if (!isOpen || deviceStateError) return;

            try {
                await handleDeviceStateFetch(deviceId);
            } catch (error) {
                // Error is handled by slice (toast), just log here
                console.log('Polling stopped due to error:', error.message);
                return; // Stop polling on error
            }

            // Only schedule next poll if component is still active, open, and no error
            if (isActive && isOpen && !deviceStateError) {
                timerId = setTimeout(poll, 1000);
            }
        };

        if (isOpen && !deviceStateError) {
            poll();
        }

        return () => {
            isActive = false;
            if (timerId) clearTimeout(timerId);
        };
    }, [isOpen, deviceStateError, deviceId, handleDeviceStateFetch]);

    const handleOnOpen = () => {
        // Reset retry count when opening popup
        dispatch(resetDeviceStateRetryCount());
        setIsOpen(true);

        handleTrafficAmountFetch(deviceId);
        handleQueueLengthFetch(deviceId);
        handleCongestionFetch(deviceId);

        console.log('intersection:', id, deviceId);
        window.location.hash = deviceId;
    };

    const handleOnClose = () => {
        setIsOpen(false);

        dispatch(setDeviceState({}));
        dispatch(setTrafficAmount({}));
        dispatch(setQueueLength({}));
        dispatch(setCongestion({}));
        dispatch(resetDeviceStateRetryCount());
        window.location.hash = '';
    };

    const handleTrafficAmountFetch = (deviceId) => {
        dispatch(getTrafficAmount(deviceId));
    };

    const handleQueueLengthFetch = (deviceId) => {
        dispatch(getQueueLength(deviceId));
    };

    const handleCongestionFetch = (deviceId) => {
        dispatch(getCongestion(deviceId));
    };

    const { t } = useTranslation();

    return (
        <Popup onOpen={handleOnOpen} onClose={handleOnClose}>
            <SignalGroup intersection={properties} />
            <Collapsible label={t('popup.statistics')}>
                <TrafficStatistics />
            </Collapsible>
            <Collapsible label={t('popup.intersection_info')}>
                <IntersectionInfo intersection={properties} />
            </Collapsible>
        </Popup>
    );
};

export default TrafficLightMarkerPopup;
